/**
 * Instrumented HTTP transport for viem.
 *
 * Wraps viem's `http` transport and times every JSON-RPC request, recording a
 * per-call metric into the in-memory buffer, which collapses each flush window
 * into `rpc_usage` wide events via the client logger — no server route or
 * beacon involved. Behaviour is otherwise identical to `http()`, except that
 * `eth_chainId` is answered locally from the transport's configured chain id.
 *
 * Additionally, failed `eth_call`s are captured to Sentry with their raw call
 * data so reverting/failing on-chain reads can be investigated. Reverts of
 * *deployless* calls (initcode sent with no `to`) are exempt: the SDK fetchers
 * issue them speculatively and fall back to multicall when they revert or hit
 * the initcode size limit, so the failure is handled — it is recorded in
 * `rpc_usage` with status `handled` (not counted as an RPC failure) and is not
 * captured to Sentry.
 */
import { http, type HttpTransportConfig, numberToHex } from 'viem'

import type { ChainId } from '@morpho-org/blue-sdk'

import { logger } from '@/lib/observability/logger'
import { botIdFallbackFetch } from '@/lib/observability/client/botid-script'

import { createDurationTimer } from '../../utils/metric-beacon'

import { recordRpcCallSample } from './buffer'
import {
  BLOCKED_RPC_ERROR_TYPES,
  classifyRpcError,
  describeRpcError,
  isInterruptedRpcError,
  type RpcErrorType
} from './error-classifier'
import {
  isBrowserOffline,
  isPageHidden,
  startPageLifecycleTracking,
  wasPageHiddenSince
} from './page-lifecycle'

type HttpTransport = ReturnType<typeof http>

/**
 * Whether an `eth_call` is a deployless read: the call object carries `data`
 * (initcode) but no `to`, so the node executes the bytecode's constructor.
 */
export function isDeploylessEthCall(requestParams: unknown): boolean {
  if (!Array.isArray(requestParams)) return false
  const [call] = requestParams
  if (!call || typeof call !== 'object') return false
  const hasTo = 'to' in call && call.to != null
  const hasData =
    ('data' in call && typeof call.data === 'string') ||
    ('input' in call && typeof call.input === 'string')
  return !hasTo && hasData
}

/**
 * Captures an `eth_call` failure to Sentry with the raw request params, so a
 * failing/reverting on-chain read can be traced back to its exact call data.
 */
function captureEthCallError(params: {
  chainId: number
  error: unknown
  errorType: RpcErrorType
  durationMs: number
  requestParams: unknown
}): void {
  const error = params.error instanceof Error ? params.error : new Error(String(params.error))

  logger.error(error, {
    chainId: params.chainId,
    tags: {
      method: 'eth_call',
      type: params.errorType
    },
    extra: {
      duration_ms: params.durationMs,
      request_params: params.requestParams
    }
  })
}

export function instrumentedHttp(
  url: string,
  config?: HttpTransportConfig & { chainId: ChainId }
): HttpTransport {
  startPageLifecycleTracking()

  const chainId = config?.chainId ?? 0
  const originalTransport = http(url, {
    ...config,
    fetchFn: config?.fetchFn ?? botIdFallbackFetch
  })

  return params => {
    const result = originalTransport(params)
    const originalRequest = result.request

    // oxlint-disable-next-line typescript/consistent-type-assertions
    result.request = (async (args, options) => {
      if (config?.chainId !== undefined && args.method === 'eth_chainId') {
        return numberToHex(config.chainId)
      }

      const timer = createDurationTimer()
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const method = (args.method as string) || 'unknown'
      const startedAt = performance.now()
      const startedHidden = isPageHidden()

      // Forward viem's per-call options (e.g. `dedupe`, retry overrides passed
      // by actions like `getChainId`) untouched — the wrapper only observes,
      // except that configured `eth_chainId` is answered locally.
      try {
        const response = await originalRequest(args, options)
        recordRpcCallSample({
          chainId,
          method,
          durationSeconds: timer.elapsed(),
          success: true
        })
        return response
      } catch (error) {
        const durationSeconds = timer.elapsed()
        const errorType = classifyRpcError(error, {
          pageHidden: startedHidden || wasPageHiddenSince(startedAt),
          offline: isBrowserOffline()
        })

        const isHandledDeploylessFailure =
          method === 'eth_call' &&
          (errorType === 'execution_reverted' || errorType === 'code_size_limit') &&
          isDeploylessEthCall(args.params)

        recordRpcCallSample({
          chainId,
          method,
          durationSeconds,
          success: false,
          errorType,
          errorMessage: describeRpcError(error),
          handled: isHandledDeploylessFailure
        })

        const isBlockedRequest = BLOCKED_RPC_ERROR_TYPES.has(errorType)

        if (
          method === 'eth_call' &&
          args.params &&
          !isHandledDeploylessFailure &&
          !isBlockedRequest &&
          !isInterruptedRpcError(errorType)
        ) {
          captureEthCallError({
            chainId,
            error,
            errorType,
            durationMs: durationSeconds * 1000,
            requestParams: args.params
          })
        }

        throw error
      }
    }) as typeof originalRequest

    return result
  }
}
